import {
  ENGAGEMENT__SUBMITTED_STAGES
} from '../../../dictionaries/Engagement.dic';
import {
  Arr
} from '../../../lib/Array.lib';
import {
  YES
} from '../../../lib/Boolean.lib';
import globalErrorHandler from '../../../lib/Error/globalErrorHandler.fun';
import {
  mapCandidates
} from '../../../lib/models/candidate';
import {
  mapEngagements
} from '../../../lib/models/engagement';
import {
  Obj
} from '../../../lib/Object.lib';
import {
  COLLECTION__CANDIDATES,
  COLLECTION__ENGAGEMENTS,
  readLoopbackRecord
} from '../../../lib/services/BE/loopback.api';
import {
  MatchPageController
} from '../MatchPage';
import evalJobCandidate from './evalJobCandidate.fun';

/**
 * Handles the change of selected job in the Match Page.
 *
 * @async
 * @function onChangeJob
 * @param {string|null} jobId - The ID of the newly selected job, or null
 *   to reset selection.
 * @returns {Promise<void>} Resolves after updating Match Page state.
 *
 * @description
 * - If `jobId` is null:
 *   - Resets the selected job and re-initializes the Match Page.
 * - If `jobId` matches the currently selected job and candidate data
 *   is already loaded:
 *   - Re-applies filtering logic (`evalJobCandidate`) with the
 *     extended filter flag, avoiding a re-fetch.
 * - If a new job is selected:
 *   - Fetches engagements for that job (only submitted stages).
 *   - Extracts candidate IDs from those engagements.
 *   - Fetches candidate data (with related engagements).
 *   - Applies `evalJobCandidate` filtering with respect to the
 *     extended filter flag.
 *   - Updates Match Page state with:
 *     - Full candidate list
 *     - Filtered matches
 *     - First candidate as selected match
 *     - Selected job ID
 * - Errors are caught and delegated to `globalErrorHandler`.
 */
export default async function onChangeJob(jobId) {
  try {
    const MatchPage = MatchPageController();
    const selectedJobId = MatchPage.state.selectedJobId;
    const fullJobCandidatesList = MatchPage.state.fullJobCandidatesList;

    // If no jobId is provided, reset state and reinitialize the component
    if (!jobId) {
      MatchPage.setState(
        { selectedJobId: null },
        MatchPage.componentDidMount
      );
      return;
    }

    // Pre-calculate extended filter flag
    const YES__EXTENDED_FILTER = YES(MatchPage.state.yesSelectedJobExtendedFilter);

    // If the same jobId is selected and candidates are already loaded, just filter in memory
    if (
      (jobId === selectedJobId)
      &&
      YES(fullJobCandidatesList.length)
    ) {
      const filteredMatches = fullJobCandidatesList.filter(
        (candidate) => evalJobCandidate({
          candidate,
          extendedFilter: YES__EXTENDED_FILTER
        })
      );
      MatchPage.setState({
        filteredMatches,
        selectedMatch: Obj(filteredMatches[0]),
        selectedJobId: jobId
      });
      return;
    }
    else {
      // Otherwise, fetch engagements related to the job
      const engagements = Arr(
        await readLoopbackRecord({
          collection: COLLECTION__ENGAGEMENTS,
          where: {
            jobId,
            stage: { inq: ENGAGEMENT__SUBMITTED_STAGES }
          },
          fields: ['id', 'candidateId'],
          mapper: mapEngagements
        })
      );

      // Extract candidate IDs from engagements
      const candidateIds = engagements.map(
        (eng) => eng.candidateId
      );

      // Fetch candidate records along with their engagements
      const fullJobCandidatesList = Arr(
        await readLoopbackRecord({
          collection: COLLECTION__CANDIDATES + '/getList',
          method: 'post',
          where: { id: { inq: candidateIds } },
          include: [{
            relation: 'engagements',
            scope: {
              where: { stage: { inq: ENGAGEMENT__SUBMITTED_STAGES } },
              fields: [
                'id',
                'jobId',
                'candidateId',
                'stage'
              ]
            }
          }],
          mapper: mapCandidates
        })
      ).map(c => ({ ...c, ENGAGEMENTS_SUBMITTED: c.engagements }));

      // Apply filtering logic on the fetched candidates
      const filteredMatches = fullJobCandidatesList.filter(
        (candidate) => evalJobCandidate({
          candidate,
          extendedFilter: YES__EXTENDED_FILTER
        })
      );

      // Update state with new candidate list and selection
      MatchPage.setState({
        fullJobCandidatesList,
        filteredMatches,
        selectedMatch: Obj(filteredMatches[0]),
        selectedJobId: jobId
      });
    }
  }
  catch (error) {
    // Handle unexpected errors
    globalErrorHandler(error);
  }
}
